import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { WorkflowDto } from '@/api/eOrdersApi';

export const useWorkflowEdit = defineStore('workflowEdit', () => {
  const isEditMode = ref(false);
  const editableWorkflow = ref<WorkflowDto | null>(null);
  const originalWorkflow = ref<WorkflowDto | null>(null);

  function setEditMode(mode: boolean) {
    isEditMode.value = mode;
  }

  function setEditableWorkflow(workflow: WorkflowDto | null) {
    editableWorkflow.value = workflow ? JSON.parse(JSON.stringify(workflow)) : null;
    originalWorkflow.value = workflow ? JSON.parse(JSON.stringify(workflow)) : null;
  }

  function updateEditableVin(vin: string) {
    if (editableWorkflow.value?.delivery) {
      editableWorkflow.value.delivery.vin = vin;
    }
  }

  const hasChanges = computed(() => {
    if (!editableWorkflow.value || !originalWorkflow.value) return false;
    
    const orig = originalWorkflow.value;
    const edit = editableWorkflow.value;
    
    if (edit.driverId !== orig.driverId) return true;
    
    if (edit.quotation?.initialQuotationNumber !== orig.quotation?.initialQuotationNumber) return true;
    
    const origScenario = orig.quotation?.requestedDeliveryDateScenario;
    const editScenario = edit.quotation?.requestedDeliveryDateScenario;
    
    if (origScenario?.mode !== editScenario?.mode || origScenario?.date !== editScenario?.date) return true;
    
    const origDel = orig.delivery;
    const editDel = edit.delivery;
    
    if (editDel?.handoverDate !== origDel?.handoverDate) return true;
    if (editDel?.handoverMileage !== origDel?.handoverMileage) return true;
    if (editDel?.arrivedAtDealer !== origDel?.arrivedAtDealer) return true;
    if (editDel?.licensePlate !== origDel?.licensePlate) return true;
    if (editDel?.cO2 !== origDel?.cO2) return true;
    
    if (edit.replacedCar?.licensePlate !== orig.replacedCar?.licensePlate) return true;
    
    const origPOs = orig.purchaseOrders ?? [];
    const editPOs = edit.purchaseOrders ?? [];
    
    for (let i = 0; i < Math.max(origPOs.length, editPOs.length); i++) {
      const origPO = origPOs[i];
      const editPO = editPOs[i];
      
      if (editPO?.dealerExpectedDeliveryDate !== origPO?.dealerExpectedDeliveryDate) return true;
      if (editPO?.supplierInternalOrderNumber !== origPO?.supplierInternalOrderNumber) return true;
      if (JSON.stringify(editPO?.contactEmailForPO) !== JSON.stringify(origPO?.contactEmailForPO)) return true;
    }
    
    return false;
  });
  
  return {
    isEditMode,
    editableWorkflow,
    originalWorkflow,
    hasChanges,
    setEditMode,
    setEditableWorkflow,
    updateEditableVin,
  };
});
